import { ThemeProvider, useTheme } from '@mui/material';
import { resolveSerpColors } from '../../api/src/fakes/serp-appearance';
import './serp-theme';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { api } from './api/client';
import type { User, Workspace } from './api/types';

interface WorkspaceContextValue {
  user: User;
  workspaces: Workspace[];
  activeWorkspace: Workspace | null;
  loading: boolean;
  error: string;
  selectWorkspace(id: string): void;
  createWorkspace(payload: { name: string; siteUrl: string }): Promise<Workspace>;
  refreshWorkspaces(): Promise<void>;
  refreshUser(): Promise<void>;
}

const WorkspaceContext = createContext<WorkspaceContextValue | null>(null);
const STORAGE_KEY = 'seo-dashboard.active-workspace';

export function WorkspaceProvider({
  user,
  refreshUser,
  children,
}: {
  user: User;
  refreshUser(): Promise<void>;
  children: ReactNode;
}) {
  const [workspaces, setWorkspaces] = useState<Workspace[]>([]);
  const [activeId, setActiveId] = useState(() => localStorage.getItem(STORAGE_KEY) ?? '');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const refreshWorkspaces = useCallback(async () => {
    const rows = await api.workspaces();
    setWorkspaces(rows);
    setActiveId((current) => {
      const next = rows.some((item) => item.id === current) ? current : (rows[0]?.id ?? '');
      if (next) localStorage.setItem(STORAGE_KEY, next);
      else localStorage.removeItem(STORAGE_KEY);
      return next;
    });
    setLoading(false);
  }, []);

  useEffect(() => {
    void refreshWorkspaces().catch((caught) => {
      setError(caught instanceof Error ? caught.message : 'Не удалось загрузить воркспейсы');
      setLoading(false);
    });
  }, [refreshWorkspaces]);

  const selectWorkspace = useCallback((id: string) => {
    localStorage.setItem(STORAGE_KEY, id);
    setActiveId(id);
  }, []);

  const createWorkspace = useCallback(
    async (payload: { name: string; siteUrl: string }) => {
      const workspace = await api.createWorkspace(payload);
      setWorkspaces((current) => [workspace, ...current]);
      selectWorkspace(workspace.id);
      return workspace;
    },
    [selectWorkspace],
  );

  const value = useMemo<WorkspaceContextValue>(
    () => ({
      user,
      workspaces,
      activeWorkspace: workspaces.find((item) => item.id === activeId) ?? null,
      loading,
      error,
      selectWorkspace,
      createWorkspace,
      refreshWorkspaces,
      refreshUser,
    }),
    [
      activeId,
      createWorkspace,
      error,
      loading,
      refreshWorkspaces,
      refreshUser,
      selectWorkspace,
      user,
      workspaces,
    ],
  );

  const outerTheme = useTheme();
  const theme = useMemo(
    () => ({ ...outerTheme, serpColors: resolveSerpColors(value.activeWorkspace?.serpColors) }),
    [outerTheme, value.activeWorkspace?.serpColors],
  );
  return (
    <WorkspaceContext.Provider value={value}>
      <ThemeProvider theme={theme}>{children}</ThemeProvider>
    </WorkspaceContext.Provider>
  );
}

export function useWorkspace(): WorkspaceContextValue {
  const value = useContext(WorkspaceContext);
  if (!value) throw new Error('useWorkspace must be used within WorkspaceProvider');
  return value;
}
